import {
  buildInviteLinkCopyText,
  displayReferralShareUrl,
  pickReferralShareEmail,
  pickReferralShareMessage,
  type ReferralRewardType,
  type ShareVariantId,
} from '@littlebird/ui-utils';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { XTwitterIcon } from '@/assets/icons/x-twitter';
import { Button } from '@/components/ui/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { handleExternalUrlClick, handleMailTo } from '@/utils/platform';

import { DraftWithLittlebird } from './draft-with-littlebird';
import { useSendInvites } from './referral.api';
import { REFERRAL_CREDIT } from './referral.constants';
import { type ReferralCodeResponse } from './referral.types';

interface ReferTabProps {
  code: ReferralCodeResponse | undefined;
  isLoading: boolean;
  onClose: () => void;
}

export function ReferTab({ code, isLoading, onClose }: ReferTabProps) {
  const isActivity = code?.reward_type === 'activity_based';
  const footnote = isActivity
    ? `1 month free = ${REFERRAL_CREDIT.ACTIVITY} in credits, no credit card needed. No limit on referrals.`
    : `2 months of Plus free = ${REFERRAL_CREDIT.PAYMENT} in credits, applied toward your next subscription payment. No limit on referrals.`;

  return (
    <YStack className="gap-5">
      <YStack className="gap-2">
        <InviteLink code={code} isLoading={isLoading} />
        <ShareCode code={code} isLoading={isLoading} />
      </YStack>
      <ShareOnSocials code={code?.referral_code} isActivity={isActivity} />
      <DraftWithLittlebird
        code={code?.referral_code}
        isActivity={isActivity}
        onNavigateAway={onClose}
      />
      <SendInvitesAccordion />
      <p className="text-xs leading-relaxed text-foreground-muted">
        {footnote}
      </p>
    </YStack>
  );
}

function InviteLink({
  code,
  isLoading,
}: {
  code: ReferralCodeResponse | undefined;
  isLoading: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(
        buildInviteLinkCopyText(code.referral_code)
      );
    } catch {
      toast.error('Failed to copy link');
      return;
    }
    setCopied(true);
    trackEvent('REFERRAL_INVITE_COPIED', {
      referral_code: code.referral_code,
      experiment_variant: code.reward_type ?? 'payment_based',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <XStack className="gap-2">
      <Input
        value={
          isLoading || !code
            ? ''
            : displayReferralShareUrl('invite_link', code.referral_code)
        }
        placeholder={isLoading ? 'Loading...' : undefined}
        readOnly
        className="flex-1 font-mono text-sm"
      />
      <Button
        onClick={handleCopy}
        className="shrink-0 px-4"
        disabled={isLoading || !code?.referral_code}
      >
        {copied ? 'Copied!' : 'Copy link'}
      </Button>
    </XStack>
  );
}

function ShareCode({
  code,
  isLoading,
}: {
  code: ReferralCodeResponse | undefined;
  isLoading: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const referralCode = code?.referral_code;

  const handleCopy = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
    } catch {
      toast.error('Failed to copy code');
      return;
    }
    setCopied(true);
    trackEvent('REFERRAL_CODE_COPIED', {
      referral_code: referralCode,
      experiment_variant: code?.reward_type ?? 'payment_based',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <XStack className="items-center gap-2">
      <span className="text-sm text-foreground-muted">Or copy code</span>
      <button
        type="button"
        onClick={handleCopy}
        disabled={isLoading || !referralCode}
        className={cn(
          'rounded-md border border-border-strong bg-secondary px-2.5 py-1',
          'font-mono text-xs font-medium tracking-wide',
          'transition-colors hover:bg-secondary-hover',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'focus-ring'
        )}
      >
        {copied ? 'copied' : isLoading ? '…' : (referralCode ?? '—')}
      </button>
    </XStack>
  );
}

type ShareChannel = 'x' | 'linkedin' | 'email';

function handleReferralShare(
  channel: ShareChannel,
  code: string,
  rewardType: ReferralRewardType
) {
  let variantId: ShareVariantId;
  if (channel === 'email') {
    const email = pickReferralShareEmail(code);
    variantId = email.variantId;
    handleMailTo('', email.subject, email.body);
  } else {
    const message = pickReferralShareMessage(channel, code);
    variantId = message.variantId;
    // LinkedIn's official share-offsite endpoint accepts only a URL and
    // opens an empty composer; the feed composer's `text` param is the
    // only way to prefill a post. It's unofficial but widely relied on,
    // and LinkedIn builds the link preview from the URL inside the text.
    const shareUrl =
      channel === 'x'
        ? `https://x.com/intent/post?text=${encodeURIComponent(message.text)}`
        : `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(message.text)}`;
    handleExternalUrlClick(shareUrl);
  }

  trackEvent('REFERRAL_SHARE_BUTTON_CLICKED', {
    platform: channel,
    message_variant: variantId,
    experiment_variant: rewardType,
  });
  trackEvent('REFERRAL_SHARE_BUTTON_FINISHED', {
    platform: channel,
    message_variant: variantId,
    experiment_variant: rewardType,
  });
}

function ShareOnSocials({
  code,
  isActivity,
}: {
  code: string | undefined;
  isActivity: boolean;
}) {
  const rewardType: ReferralRewardType = isActivity
    ? 'activity_based'
    : 'payment_based';

  const share = (channel: ShareChannel) => () =>
    code && handleReferralShare(channel, code, rewardType);

  return (
    <XStack className="items-center justify-between gap-3 rounded-xl border border-border-strong px-4 py-3">
      <p className="text-sm font-medium shrink-0">Share directly on Socials</p>
      <XStack className="gap-2 flex-wrap justify-end">
        <ShareButton disabled={!code} onClick={share('x')}>
          <XTwitterIcon className="size-3.5" />X
        </ShareButton>
        <ShareButton disabled={!code} onClick={share('linkedin')}>
          <Icons.linkedin className="size-3.5" />
          LinkedIn
        </ShareButton>
        <ShareButton disabled={!code} onClick={share('email')}>
          <Icons.invite className="size-3.5" />
          Email
        </ShareButton>
      </XStack>
    </XStack>
  );
}

function ShareButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5 px-2.5"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function SendInvitesAccordion() {
  const [input, setInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const { sendInvites, loading } = useSendInvites();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTags = input
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t && !tags.includes(t));
      if (newTags.length) setTags([...tags, ...newTags]);
      setInput('');
    }
  };

  const handleSend = async () => {
    const inputEmails = input
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e && !tags.includes(e));
    const allEmails = [...tags, ...inputEmails];
    if (!allEmails.length) return;

    await sendInvites(allEmails);
    setTags([]);
    setInput('');
  };

  const hasEmails = tags.length > 0 || input.trim().length > 0;

  return (
    <Collapsible>
      <CollapsibleTrigger className="group flex w-full items-center gap-1.5 py-1 text-sm text-foreground-muted hover:text-foreground">
        <Icons.chevronRight className="size-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
        Send invites by email
      </CollapsibleTrigger>
      <CollapsibleContent>
        <YStack className="gap-2 pt-3">
          <XStack className="gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="email@example.com"
              className="flex-1"
            />
            <Button
              onClick={handleSend}
              disabled={!hasEmails || loading}
              isLoading={loading}
              className="w-24"
            >
              Send
            </Button>
          </XStack>
          {tags.length > 0 && (
            <XStack className="flex-wrap gap-2">
              {tags.map((tag) => (
                <XStack
                  key={tag}
                  onClick={() => setTags(tags.filter((t) => t !== tag))}
                  className="cursor-pointer items-center gap-2 rounded-sm border border-border-strong bg-secondary px-2 py-1 text-sm"
                >
                  {tag}
                  <Icons.cancel className="size-3" />
                </XStack>
              ))}
            </XStack>
          )}
        </YStack>
      </CollapsibleContent>
    </Collapsible>
  );
}
